import { useEffect, useState } from "react";
import { Activity, Eye, LogIn, Loader2, RefreshCw, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { adminApi } from "@/lib/adminApi";

function relativeTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!t) return iso;
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

const StatCard = ({ Icon, label, value, sub }) => (
  <div className="bg-zinc-950 border border-white/10 rounded-sm p-4">
    <div className="flex items-center gap-3">
      <Icon className="w-5 h-5 text-burns-gold" />
      <div className="flex-1">
        <p className="text-zinc-400 text-xs">{label}</p>
        <p className="font-heading text-2xl text-white leading-tight">{value}</p>
        {sub && <p className="text-zinc-500 text-xs">{sub}</p>}
      </div>
    </div>
  </div>
);

// Admin tab — overview + per-parent table for parent portal usage analytics.
export default function ParentPortalStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/parent-portal-stats");
      setData(r.data);
    } catch (e) {
      // adminApi handles 401 globally
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-burns-gold" />
      </div>
    );
  }
  if (!data) return null;

  const { totals, per_parent, recent_events } = data;
  const adopted = totals.parents_in_registrations
    ? Math.round((totals.unique_parents_logged_in / totals.parents_in_registrations) * 100)
    : 0;

  const filtered = search.trim()
    ? per_parent.filter((p) => (p.email || "").toLowerCase().includes(search.toLowerCase()))
    : per_parent;

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-2xl font-bold text-white">PARENT PORTAL STATS</h2>
          <p className="text-zinc-500 text-sm">Logins and dashboard activity by parent.</p>
        </div>
        <Button onClick={load} variant="ghost" className="text-zinc-400 hover:text-white">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard
          Icon={LogIn}
          label="Logins"
          value={totals.total_logins}
          sub={`${totals.unique_parents_logged_in} unique parents`}
        />
        <StatCard
          Icon={Eye}
          label="Page views"
          value={totals.total_page_views}
        />
        <StatCard
          Icon={Users}
          label="Accounts created"
          value={totals.parents_with_account}
          sub={`of ${totals.parents_in_registrations} registered parents`}
        />
        <StatCard
          Icon={Activity}
          label="Adoption"
          value={adopted + "%"}
          sub="have signed in at least once"
        />
      </div>

      <div className="bg-zinc-950 border border-white/10 rounded-sm">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
          <h3 className="text-white font-heading text-sm">PER PARENT</h3>
          <Input
            type="search"
            placeholder="Search email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-transparent border-white/20 text-white max-w-xs ml-auto"
          />
        </div>
        {filtered.length === 0 ? (
          <p className="text-zinc-500 text-sm p-6">No parent activity yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full admin-table text-sm">
              <thead>
                <tr className="bg-burns-dark text-zinc-400">
                  <th className="text-left">Email</th>
                  <th>Logins</th>
                  <th>Page views</th>
                  <th>Last login</th>
                  <th>Last active</th>
                  <th>Account?</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.email}>
                    <td className="text-white font-medium">{p.email}</td>
                    <td className="text-burns-gold">{p.login_count}</td>
                    <td className="text-zinc-300">{p.page_view_count}</td>
                    <td className="text-zinc-400" title={p.last_login_at || ""}>{relativeTime(p.last_login_at)}</td>
                    <td className="text-zinc-400" title={p.last_active_at || ""}>{relativeTime(p.last_active_at)}</td>
                    <td>
                      {p.has_account ? (
                        <span className="text-green-400 text-xs">Yes</span>
                      ) : (
                        <span className="text-zinc-500 text-xs">No</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {recent_events?.length > 0 && (
        <div className="bg-zinc-950 border border-white/10 rounded-sm mt-6">
          <h3 className="text-white font-heading text-sm px-4 py-3 border-b border-white/10">RECENT EVENTS (last 20)</h3>
          <div className="overflow-x-auto">
            <table className="w-full admin-table text-sm">
              <thead>
                <tr className="bg-burns-dark text-zinc-400">
                  <th className="text-left">When</th>
                  <th className="text-left">Parent</th>
                  <th className="text-left">Event</th>
                  <th className="text-left">Page</th>
                </tr>
              </thead>
              <tbody>
                {recent_events.map((e, i) => (
                  <tr key={i}>
                    <td className="text-zinc-400" title={e.occurred_at || ""}>{relativeTime(e.occurred_at)}</td>
                    <td className="text-white">{e.parent_email}</td>
                    <td className="text-zinc-300">{e.event_type}</td>
                    <td className="text-zinc-500">{e.page}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
